import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { PenLine, Upload, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { OnOffSwitch } from "@/components/OnOffSwitch";
import { SignaturePad } from "@/components/SignaturePad";
import { useApp } from "@/context/AppContext";
import { api, errMsg } from "@/lib/api";
import { useSignatures } from "@/lib/signatures";

const inputCls = "h-10 bg-white text-slate-900";

function Row({ title, hint, children, testid }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 p-4" data-testid={testid}>
      <div>
        <div className="font-semibold text-slate-900">{title}</div>
        <p className="text-sm text-slate-500">{hint}</p>
      </div>
      {children}
    </div>
  );
}

function DateSettings({ s, save }) {
  const [fixed, setFixed] = useState(s.sign_date || "");
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Row title="Show signing date" hint="Print a date line next to each signature." testid="sig-date-row">
        <OnOffSwitch checked={s.show_date !== false} onChange={(v) => save({ signatures: { show_date: v } })} testid="sig-date-toggle" />
      </Row>
      <Row title="Signing date" hint={s.sign_date ? "Fixed date is used on all reports." : "Empty = today's date is used."} testid="sig-fixed-date-row">
        <div className="flex flex-wrap items-center gap-2">
          <Input type="date" value={fixed} onChange={(e) => setFixed(e.target.value)} className={`${inputCls} w-44`} data-testid="sig-fixed-date-input" />
          <Button type="button" onClick={() => save({ signatures: { sign_date: fixed } })} data-testid="sig-fixed-date-save">Save date</Button>
          {s.sign_date && <Button type="button" variant="ghost" onClick={() => { setFixed(""); save({ signatures: { sign_date: "" } }); }} data-testid="sig-fixed-date-clear">Use today</Button>}
        </div>
      </Row>
    </div>
  );
}

function Person({ p, text, setText, onUploaded, hasSig }) {
  const ref = useRef(null);
  const [pad, setPad] = useState(false);
  const send = async (blob, name = "signature.png") => {
    const fd = new FormData();
    fd.append("file", blob, name);
    try { await api.post(`/signatures/${p.key}`, fd); toast.success(`${p.label} signature saved`); onUploaded(); } catch (e) { toast.error(errMsg(e)); throw e; }
  };
  const remove = async () => { try { await api.delete(`/signatures/${p.key}`); toast.success("Signature removed"); onUploaded(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="space-y-3 rounded-xl border border-slate-200 p-4" data-testid={`sig-editor-${p.key}`}>
      <div className="font-heading font-bold">{p.label}</div>
      <Input value={text[`${p.key}_name`] || ""} onChange={(e) => setText({ ...text, [`${p.key}_name`]: e.target.value })} maxLength={120} placeholder="Name" className={inputCls} data-testid={`sig-${p.key}-name`} />
      <Input value={text[`${p.key}_title`] || ""} onChange={(e) => setText({ ...text, [`${p.key}_title`]: e.target.value })} maxLength={120} placeholder="Title (e.g. Treasurer)" className={inputCls} data-testid={`sig-${p.key}-title`} />
      <div className="flex h-20 items-center justify-center rounded-lg border border-dashed border-slate-300 bg-white">
        {p.img ? <img src={p.img} alt="Signature" className="block h-auto max-h-16 w-auto max-w-full" data-testid={`sig-${p.key}-preview`} /> : <span className="text-sm text-slate-400">No signature yet</span>}
      </div>
      <div className="flex flex-wrap gap-2">
        <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) { send(f, f.name).catch(() => {}); } e.target.value = ""; }} data-testid={`sig-${p.key}-file`} />
        <Button type="button" variant="outline" onClick={() => ref.current?.click()} data-testid={`sig-${p.key}-upload`}><Upload className="mr-2 h-4 w-4" /> Upload</Button>
        <Button type="button" variant="outline" onClick={() => setPad(true)} data-testid={`sig-${p.key}-draw`}><PenLine className="mr-2 h-4 w-4" /> Draw</Button>
        {hasSig && <Button type="button" variant="ghost" onClick={remove} data-testid={`sig-${p.key}-remove`}><Trash2 className="mr-2 h-4 w-4 text-red-600" /> Remove</Button>}
      </div>
      <SignaturePad open={pad} onClose={() => setPad(false)} label={p.label} onSave={(b) => send(b)} />
    </div>
  );
}

export function AdminSignatures({ save }) {
  const { settings, reload } = useApp();
  const s = settings.signatures || {};
  const [text, setText] = useState({ prepared_name: s.prepared_name, prepared_title: s.prepared_title, concurred_name: s.concurred_name, concurred_title: s.concurred_title });
  useEffect(() => { setText({ prepared_name: s.prepared_name, prepared_title: s.prepared_title, concurred_name: s.concurred_name, concurred_title: s.concurred_title }); }, [settings.report_sec]); // eslint-disable-line react-hooks/exhaustive-deps
  const people = useSignatures() || [{ key: "prepared", label: "Prepared by" }, { key: "concurred", label: "Concurred by" }];
  return (
    <section className="space-y-4 rounded-xl border border-slate-200 bg-white p-5" data-testid="admin-signatures">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="font-heading text-lg font-bold">Report signatures</h3>
          <p className="text-sm text-slate-500">Shown at the bottom of every report and its PDF, Excel and picture exports.</p>
        </div>
        <div className="flex items-center gap-2 text-sm text-slate-600">Show on reports
          <OnOffSwitch checked={s.show !== false} onChange={(v) => save({ signatures: { show: v } })} testid="sig-show-toggle" />
        </div>
      </div>
      <DateSettings key={settings.report_sec} s={s} save={save} />
      <div className="grid gap-4 lg:grid-cols-2">
        {people.map((p) => <Person key={p.key} p={p} text={text} setText={setText} hasSig={!!s[`${p.key}_sig`]} onUploaded={reload} />)}
      </div>
      <div className="flex justify-end"><Button onClick={() => save({ signatures: text })} data-testid="sig-save-btn">Save names & titles</Button></div>
    </section>
  );
}
